import React, { useContext, useState, useEffect, useRef } from 'react'
import TextareaAutosize from 'react-textarea-autosize'
import { Badge, Button, ButtonGroup, DropdownItem, DropdownMenu, DropdownToggle, Label, UncontrolledDropdown } from 'reactstrap'
import axios from '../shared/axios'
import { ChevronLeft, ChevronRight, Menu } from 'react-feather'
import { store } from '../../stores/multipost'

// Wraps a number around a range (min inclusive, max exclusive)
const wrap = (n, max) => ((n % max) + max) % max

export function SidIcon({mood = null, className = ""}){
  const { suggestionsIcon } = useContext(store)

  switch(mood){
    case "thinking":
      return (
        <div className={className}>
          <svg className="sid-top sid-top-offset" width="55" height="41" viewBox="0 0 417 311">
            <use xlinkHref={`${suggestionsIcon}#thinking`} />
          </svg>
        </div>
      )
    case "success":
      return (
        <div className={className}>
          <svg className="sid-top sid-top-offset" width="55" height="41" viewBox="0 0 417 311">
            <use xlinkHref={`${suggestionsIcon}#success`} />
          </svg>
        </div>
      )
    default:
      return (
        <div className={className}>
          <svg className="sid-top sid-top-offset" width="55" height="41" viewBox="0 0 417 311">
            <use xlinkHref={`${suggestionsIcon}#default`} />
          </svg>
        </div>
      )
  }
}

export function TextareaWithSuggestions({
  value,
  onChange,
  className = "",
  placeholder = "This message is optional…",
  minRows = 5,
  id,
  name,
  disabled = false,
  showTrigger = true,
  showSuggestions = false,
  suggestions = {
    value: [],
    error: false,
    loading: false,
  },
  resetSuggestions = () => {},
  fetchSuggestions = () => {},
}) {

  const textareaRef = useRef()
  const [activeSuggestion, setActiveSuggestion] = useState(null)
  const [originalMessage, setOriginalMessage] = useState(value)
  const [containerHeight, setContainerHeight] = useState(100)
  const [isFocused, setIsFocused] = useState(false)

  useEffect(() => {
    if (activeSuggestion !== null && suggestions?.value?.length) {
      onChange({ target: { value: suggestions.value[activeSuggestion].message } })
    }
  }, [activeSuggestion, suggestions])

  useEffect(() => {
    if (suggestions?.value?.length && !activeSuggestion){
      setOriginalMessage(textareaRef.current.value)
      setActiveSuggestion(0)
    }else{
      setActiveSuggestion(null)
    }
  }, [suggestions, textareaRef])


  const handleCancelSuggestions = () => {
    setActiveSuggestion(null)
    resetSuggestions()
    onChange({ target: { value: originalMessage } })
    setOriginalMessage(null)
  }

  const handleRevertSuggestion = () => {
    onChange({ target: { value: suggestions.value[activeSuggestion].message } })
  }

  const handleTextareaHeightChange = (height) => {
    // 65px leaves room for suggestions UI
    setContainerHeight(showSuggestions ? height + 65 : height)
  }

  return (
    <div
      className={`form-control border-0 shadow-none position-relative p-0 cursor-text ${className} ${isFocused ? 'focus' : ''}`}
      style={{ minHeight: containerHeight, background: (disabled ? '#f8f8f8' : 'transparent')  }}
      onClick={()=> textareaRef.current?.focus() }
    >
      <TextareaAutosize
        ref={textareaRef}
        className="form-control border-0 shadow-none p-3"
        style={{lineHeight: 1.3}}
        id={id}
        name={name}
        placeholder={placeholder}
        minRows={minRows}
        value={value}
        onHeightChange={handleTextareaHeightChange}
        onChange={onChange}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        disabled={disabled}
      />

      {showSuggestions && (
        <div
          className="position-absolute bottom-0 start-0 end-0 rounded-bottom-3 p-2"
          aria-live="polite"
          aria-atomic="true"
        >
          {suggestions.value.length > 0 ? (
            <div className="d-flex align-items-center">
              <Button
                color="outline-muted"
                className="d-none d-md-block ms-1"
                onClick={handleRevertSuggestion}
                disabled={value == suggestions?.value?.[activeSuggestion]?.message}
              >
                Revert
              </Button>
              <Button
                color="outline-muted"
                className="d-none d-md-block ms-2 me-auto"
                onClick={handleCancelSuggestions}
              >
                Cancel
              </Button>
              <UncontrolledDropdown className="d-md-none ms-1 me-auto">
                <DropdownToggle color="outline-muted">
                  <Menu size="20" className="icon-valign icon-btn-size" />
                </DropdownToggle>
                <DropdownMenu>
                  <DropdownItem
                    disabled={value == suggestions?.value?.[activeSuggestion]?.message}
                    onClick={handleRevertSuggestion}
                  >
                    Revert
                  </DropdownItem>
                  <DropdownItem onClick={handleCancelSuggestions}>Cancel</DropdownItem>
                </DropdownMenu>
              </UncontrolledDropdown>
              <ButtonGroup className="btn-group-outline ms-1">
                <Button
                  color="outline-muted"
                  onClick={() => setActiveSuggestion(i => wrap(i - 1, suggestions.value.length))}
                >
                  <ChevronLeft size="20" className="icon-valign icon-btn-size" />
                </Button>
                <Button
                  color="outline-muted"
                  onClick={() => setActiveSuggestion(i => wrap(i + 1, suggestions.value.length))}
                >
                  <ChevronRight size="20" className="icon-valign icon-btn-size" />
                </Button>
                <Button
                  color="outline-muted"
                  className="arrow-right text-dark"
                  disabled={true}
                >
                  <span className="d-none d-md-inline">Option </span>{activeSuggestion + 1} of {suggestions.value.length}
                </Button>
              </ButtonGroup>
              <SidIcon mood="success" className="ms-1" />
            </div>
          ) : (
            <div className="d-flex align-items-center justify-content-end">
              {suggestions.loading && (
                <>
                  <Button
                    color="outline-muted arrow-right"
                    className="text-dark"
                    disabled={true}
                    onClick={fetchSuggestions}
                  >
                    <div className="typing">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </Button>
                  <SidIcon mood="thinking" className="ms-1" />
                </>
              )}
              {!suggestions.loading && !suggestions.error && showTrigger && (
                <>
                  <Button
                    color="outline-muted arrow-right"
                    className="text-dark"
                    onClick={fetchSuggestions}
                    disabled={disabled}
                  >
                    Want a few suggestions?
                    <span className="badge bg-gray-darker text-dark team-inset-button ms-2">
                      Generate
                    </span>
                  </Button>
                  <SidIcon className="ms-1" />
                </>
              )}
              {suggestions.error && (
                <>
                  <Button
                    color="outline-muted arrow-right"
                    className="text-danger"
                    onClick={fetchSuggestions}
                    disabled={disabled}
                  >
                    Yikes, something went wrong. Try again?
                  </Button>
                  <SidIcon mood="thinking" className="ms-1" />
                </>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}